/**
 * Buscar un artículo, desde cualquier pantalla.
 *
 * Estaba repetido en cuatro lugares —punto de venta, entrada de stock (dos
 * veces) y ajuste de inventario— y los cuatro buscaban solo por nombre y por
 * código de barras. Buscar «coral» no encontraba el «Balde de Pintura Coral»
 * porque Coral es la marca, que es un campo que la pantalla muestra y por el
 * que es natural buscar.
 *
 * Con cuatro copias, mejorar la búsqueda significaba acordarse de las cuatro.
 * Acá hay una.
 *
 * Qué se mira: nombre, código de barras, marca y categoría. Son los datos que
 * el usuario ve en la lista; buscar por algo que no está a la vista
 * desconcierta más de lo que ayuda.
 */

/**
 * Deja un texto comparable: sin mayúsculas, sin tildes, sin espacios de sobra.
 *
 * Las tildes importan de verdad acá. En una ferretería hay «cinta métrica»,
 * «válvula» y «caño», y nadie las tipea con tilde en el mostrador. Sin
 * normalizar, buscar «cano» no encuentra «caño» y el artículo parece no
 * existir.
 */
export function normalizar(texto) {
    return String(texto ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')  // marcas diacríticas
        .replace(/\s+/g, ' ')
        .trim();
}

/** Los campos de un artículo en los que tiene sentido buscar. */
const CAMPOS = ['nombre', 'codigo_barras', 'marca', 'categoria_nombre'];

/** Si un artículo coincide con lo buscado. */
export function coincide(producto, termino) {
    const buscado = normalizar(termino);
    if (!buscado) return true;

    // Cada palabra tiene que aparecer en algún campo, no necesariamente en el
    // mismo. Así «balde coral» encuentra el balde de la marca Coral, que es
    // como la gente busca cuando recuerda dos datos sueltos.
    const palabras = buscado.split(' ');
    const texto = CAMPOS.map(campo => normalizar(producto?.[campo])).join(' ');
    return palabras.every(palabra => texto.includes(palabra));
}

/** Los artículos que coinciden, en el orden en que vinieron. */
export function buscarProductos(productos, termino) {
    if (!Array.isArray(productos)) return [];
    return productos.filter(p => coincide(p, termino));
}

/**
 * El artículo que corresponde sin ambigüedad, o null.
 *
 * Sirve para el lector de código de barras: escanear tiene que agregar el
 * artículo directo, sin obligar a elegir de una lista. Un código exacto gana
 * sobre cualquier coincidencia parcial; si no hay exacto, solo se resuelve
 * cuando queda un único candidato. Con dos o más no se adivina: agregar el
 * artículo equivocado a una venta es peor que pedir que se elija.
 */
export function unicoProducto(productos, termino) {
    const buscado = String(termino ?? '').trim();
    if (!buscado) return null;

    const exacto = (productos || []).find(p => p.codigo_barras === buscado);
    if (exacto) return exacto;

    const candidatos = buscarProductos(productos, buscado);
    return candidatos.length === 1 ? candidatos[0] : null;
}
